import { useGetAllProductsLocationsByWarehouse } from 'src/pages/warehouses/warehouseRequests';
import { buildProductSearchIndex, findMatchingProductIds, getProductLocationAvailable } from 'src/pages/warehouses/relocate/useProductLocationsTable';
import type { ProductLocationRow, ProductLocationsTableData } from 'src/pages/warehouses/relocate/useProductLocationsTable';
import { ProductLocationPrintButton } from 'src/shared/printing/ProductLocationLabelPrinting';
import { useEffect, useMemo } from 'react';
import type { MouseEvent } from 'react';
import type { GridColDef } from '@mui/x-data-grid-premium';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CustomButton from 'src/core/ui/CustomButton';
import tableHelper from 'src/core/tables/tableHelper';
import { useTableSettings, useInitialTableSettings, useCompleteTableSettings } from 'src/core/tables/useTableSettings';
import type { tableSettingsProps } from 'src/core/tables/useTableSettings';

// The search endpoint supplies these rows; this hook does not fetch a main table.
export const useStocktakingTable = (rows: StockCorrectionItem[], onSelect: (row: StockCorrectionItem) => void): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = useMemo<GridColDef[]>(() => [
        { field: 'select', headerName: 'Choose', width: 85, sortable: false, filterable: false, disableColumnMenu: true, renderCell: (params) => <CustomButton iconButton icon={<ArrowUpwardIcon />} tooltipText={`Choose ${params.row.ProductId}, storage item ${params.row.StorageItemId}`} onClick={(event) => { event.stopPropagation(); onSelect(params.row as StockCorrectionItem); }} /> },
        { field: 'ProductId', headerName: 'Product ID', minWidth: 130 },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', minWidth: 210 },
        { field: 'ProductName', headerName: 'Product', minWidth: 220, flex: 1 },
        { field: 'WarehouseQty', headerName: 'Warehouse qty', type: 'number', minWidth: 135 },
        { field: 'WarehouseName', headerName: 'Warehouse', minWidth: 190 },
        { field: 'StorageItemId', headerName: 'Storage item ID', minWidth: 140 },
        { field: 'WarehouseItemId', headerName: 'Warehouse item ID', minWidth: 165 },
    ], [onSelect]);
    const tableSettings = useTableSettings({ tableName: 'StockCorrectionMatchesTable', idPath: storageItemIdPath, columns, rows, hasToolbar: false, autoHeight: true });
    tableSettings.loading = false;

    useEffect(() => {
        const selection = tableSettings.rowSelectionModel ?? [];
        if (!Array.isArray(selection) || selection.length === 0) return;
        const selectedRow = rows.find((row) => String(row.WarehouseItemId) === String(selection[0]));
        tableSettings.setRowSelectionModel?.([]);
        if (selectedRow) onSelect(selectedRow);
    }, [tableSettings.rowSelectionModel, rows, onSelect]);

    return [columns, tableSettings];
};

// Shared by the form card and the expanded rows in the selection dialog.
export const useWarehouseItemLocationsTable = (rows: WarehouseItemLocation[], tableName = 'StockCorrectionItemLocationsTable'): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = useMemo<GridColDef[]>(() => [
        { field: 'LocationLabel', headerName: 'Location', minWidth: 190, flex: 1 },
        { field: 'Quantity', headerName: 'Quantity', type: 'number', minWidth: 105 },
        { field: 'QuantityReserved', headerName: 'Reserved', type: 'number', minWidth: 105 },
        { field: 'PartitionName', headerName: 'Partition', minWidth: 155 },
        { field: 'LastCountDate', headerName: 'Last counted', type: 'dateTime', minWidth: 175, valueGetter: tableHelper.getDateColumn },
        { field: 'LastCountBy', headerName: 'Counted by', minWidth: 170 },
        { field: 'LocationId', headerName: 'Location ID', minWidth: 115 },
    ], []);
    const tableSettings = useTableSettings({ tableName, idPath: locationIdPath, columns, rows, hasToolbar: false, autoHeight: true, hideFooter: true });
    tableSettings.loading = false;

    return [columns, tableSettings];
};

// Separate columns and persisted settings let stock correction evolve independently.
// Zero available quantity does not prevent correcting a location's recorded stock.
export const useStockCorrectionLocationsTable = (warehouseId: Identifier, refreshKey?: unknown, onSelect?: (row: ProductLocationRow) => void, disabled = false): [columns: GridColDef[], tableSettings: tableSettingsProps, data: ProductLocationsTableData] => {
    const { rows: responseRows, status } = useGetAllProductsLocationsByWarehouse(warehouseId, refreshKey);
    const rows = useMemo<ProductLocationRow[]>(() => (Array.isArray(responseRows) ? responseRows : []).filter((row) => String(row.WarehouseId) === String(warehouseId)).map((row) => ({ ...row, ProductId: String(row.ProductId ?? row.ArticleNumber ?? '') })), [responseRows, warehouseId]);
    const searchIndex = useMemo(() => buildProductSearchIndex(rows), [rows]);
    const initialTableSettings = useInitialTableSettings({ tableName: 'StockCorrectionLocationsTable', idPath: ['WarehouseItemLocationId'], defaultColumnVisibilityModel: { WarehouseId: false, WarehouseName: false }, checkboxSelection: false, disableSelectionOnClick: true, status }, rows);
    const selectionDisabled = disabled || initialTableSettings.loading || Number(status) >= 400 || /error|fail|abort|cancel|timeout/i.test(String(status ?? ''));

    const columns = useMemo<GridColDef[]>(() => {
        const definitions: GridColDef[] = [
            { field: 'printProductLabel', headerName: '', width: 80, sortable: false, filterable: false, disableColumnMenu: true, disableExport: true, renderCell: (params) => <ProductLocationPrintButton productId={params.row.ProductId} available={getProductLocationAvailable(params.row)} location={params.row.Location} disabled={selectionDisabled} tabIndex={params.hasFocus ? 0 : -1} /> },
            ...(onSelect ? [{ field: 'select', headerName: 'Select', width: 80, sortable: false, filterable: false, disableColumnMenu: true, renderCell: (params) => <CustomButton iconButton icon={<ArrowUpwardIcon />} disabled={selectionDisabled} tooltipText={`Select stock correction row ${params.row.WarehouseItemLocationId}`} onClick={(event: MouseEvent) => { event.stopPropagation(); onSelect(params.row as ProductLocationRow); }} /> } as GridColDef] : []),
            { field: 'WarehouseItemLocationId', headerName: 'Id', width: 90 },
            { field: 'ProductId', headerName: 'ProductId', width: 140, getApplyQuickFilterFn: (value) => { const matches = findMatchingProductIds(searchIndex, value); return (cellValue) => matches.has(String(cellValue)); } },
            { field: 'Name', headerName: 'Name', flex: 1, minWidth: 200 },
            { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id', width: 190 },
            { field: 'EAN', headerName: 'Barcode(EAN)', width: 140 },
            { field: 'Location', headerName: 'Location', flex: 1, minWidth: 200 },
            { field: 'Quantity', headerName: 'Quantity', width: 110, type: 'number' },
            { field: 'QuantityReserved', headerName: 'Reserved Quantity', width: 150, type: 'number' },
            { field: 'QuantityAvailable', headerName: 'Available', width: 110, type: 'number', valueGetter: (_value, row) => getProductLocationAvailable(row) },
            { field: 'WarehouseName', headerName: 'Warehouse', flex: 0.6, minWidth: 160 },
            { field: 'StorageItemId', headerName: 'Storage Item Id', width: 130 },
            { field: 'LocationId', headerName: 'Location Id', width: 110 },
            { field: 'WarehouseId', headerName: 'Warehouse Id', width: 120 },
            { field: 'ModifyDate', headerName: 'Modified Date', width: 190 },
        ];
        return definitions.map((column) => column.field === 'ProductId' ? column : { ...column, getApplyQuickFilterFn: () => null });
    }, [onSelect, selectionDisabled, searchIndex]);

    const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
    return [columns, tableSettings, { rows, status }];
};

export type Identifier = string | number;

export type WarehouseItemLocation = {
    LocationId: Identifier;
    LocationLabel: string;
    PartitionName?: string | null;
    LastCountDate?: string | null;
    LastCountBy?: string | null;
    Quantity: number;
    QuantityReserved: number;
};

export type StockCorrectionItem = {
    StorageItemId: Identifier;
    WarehouseItemId: Identifier;
    ProductId: Identifier;
    ManufacturerProductId?: Identifier | null;
    ProductName: string;
    WarehouseId: Identifier;
    WarehouseName: string;
    WarehouseQty: number;
    WarehouseItemLocations: WarehouseItemLocation[];
    DefaultPictureResourceId?: Identifier | null;
};

const storageItemIdPath = ['WarehouseItemId'];
const locationIdPath = ['LocationId'];

